import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useApi from "../../hook/useApi";
import Loader from "../../components/Loader/Loader";

const toNumberOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const TaxStatusToggle = ({ id, isActive, onChange, disabled }) => (
  <div className="form-check form-switch switch-primary mb-20">
    <input
      className="form-check-input"
      type="checkbox"
      role="switch"
      id={id}
      checked={!!isActive}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <label className="form-check-label fw-medium" htmlFor={id}>
      {isActive ? "Active" : "Inactive"}
    </label>
  </div>
);

const CountryTaxLayer = () => {
  const { AsyncGetApiCall, AsyncPatchAPICall } = useApi();
  const [loading, setLoading] = useState(false);
  const [hotelForm, setHotelForm] = useState({
    hotelTaxPercent: "",
    isActive: true,
  });
  const [esimForm, setEsimForm] = useState({
    taxPercent: "",
    isActive: true,
  });

  const applyListData = (data) => {
    if (!data) return;
    setHotelForm({
      hotelTaxPercent:
        data.hotelTaxPercent !== undefined && data.hotelTaxPercent !== null
          ? data.hotelTaxPercent
          : "",
      isActive: data.isActive !== false,
    });
    const esimPercent =
      data.eSIMTaxPercent ?? data.esimTaxPercent ?? data.globalEsimTaxPercent;
    setEsimForm({
      taxPercent:
        esimPercent !== undefined && esimPercent !== null ? esimPercent : "",
      isActive: data.isActive !== false,
    });
  };

  const fetchTaxConfig = async () => {
    setLoading(true);
    const res = await AsyncGetApiCall("/country-tax/list");
    if (res?.errors) {
      toast.error(res.errors[0]);
    } else if (res?.success) {
      applyListData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTaxConfig();
    // eslint-disable-next-line
  }, []);

  const saveHotelTax = async () => {
    const hotelTaxPercent = toNumberOrNull(hotelForm.hotelTaxPercent);
    if (hotelTaxPercent == null) {
      toast.error("Hotel tax % is required (0–100).");
      return;
    }

    setLoading(true);
    const result = await AsyncPatchAPICall("/country-tax/hotel-tax", {
      hotelTaxPercent,
      isActive: !!hotelForm.isActive,
    });
    if (result?.errors) toast.error(result.errors[0]);
    else if (result?.success) {
      toast.success("Netherlands hotel tax saved.");
      if (result.data) applyListData(result.data);
      else fetchTaxConfig();
    }
    setLoading(false);
  };

  const saveEsimTax = async () => {
    const taxPercent = toNumberOrNull(esimForm.taxPercent);
    if (taxPercent == null) {
      toast.error("eSIM tax % is required (0–100).");
      return;
    }

    setLoading(true);
    const result = await AsyncPatchAPICall("/country-tax/global-esim", {
      taxPercent,
      isActive: !!esimForm.isActive,
    });
    if (result?.errors) toast.error(result.errors[0]);
    else if (result?.success) {
      toast.success("Global eSIM tax saved.");
      if (result.data) applyListData(result.data);
      else fetchTaxConfig();
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="row gy-4">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="mb-0">Netherlands hotel tax</h6>
              <p className="text-sm text-secondary-light mb-0">
                Hotel bookings in the Netherlands (NL) only. Flights have no
                country tax. Values are percentages (0–100).
              </p>
            </div>
            <div className="card-body p-24">
              <div className="mb-16">
                <label className="form-label">Country</label>
                <input
                  className="form-control"
                  value="NL — Netherlands"
                  disabled
                />
              </div>
              <div className="mb-16">
                <label className="form-label">Hotel tax %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="form-control"
                  value={hotelForm.hotelTaxPercent}
                  onChange={(e) =>
                    setHotelForm((p) => ({
                      ...p,
                      hotelTaxPercent: e.target.value,
                    }))
                  }
                  placeholder="0"
                />
              </div>
              <TaxStatusToggle
                id="hotelTaxActive"
                isActive={!!hotelForm.isActive}
                disabled={loading}
                onChange={(isActive) =>
                  setHotelForm((p) => ({ ...p, isActive }))
                }
              />
              <button
                type="button"
                className="btn btn-primary-600"
                onClick={saveHotelTax}
                disabled={loading}
              >
                Save hotel tax
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="mb-0">Global eSIM tax</h6>
              <p className="text-sm text-secondary-light mb-0">
                One rate for all eSIM purchases worldwide. Values are
                percentages (0–100).
              </p>
            </div>
            <div className="card-body p-24">
              <div className="mb-16">
                <label className="form-label">eSIM tax %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="form-control"
                  value={esimForm.taxPercent}
                  onChange={(e) =>
                    setEsimForm((p) => ({ ...p, taxPercent: e.target.value }))
                  }
                  placeholder="0"
                />
              </div>
              <TaxStatusToggle
                id="esimTaxActive"
                isActive={!!esimForm.isActive}
                disabled={loading}
                onChange={(isActive) =>
                  setEsimForm((p) => ({ ...p, isActive }))
                }
              />
              <button
                type="button"
                className="btn btn-primary-600"
                onClick={saveEsimTax}
                disabled={loading}
              >
                Save eSIM tax
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountryTaxLayer;
